/**
 * CustomTemplates — Flexible Excel/CSV Upload with Full Styling Preservation
 *
 * Allows project teams to upload any Excel or CSV file in any format.
 * Preserves all cell, row, and column styling (colors, borders, merged cells,
 * fonts, alignment) and renders a faithful reproduction of the original
 * spreadsheet in the browser.
 */

// react
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// i18n

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';

// mui
import {
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	ArrowBack as IconBack,
	CloudUpload as IconCloudUpload,
	Delete as IconDelete,
	Description as IconFile,
	FilePresent as IconExcel,
	Refresh as IconRefresh,
	Save as IconSave,
	TableChart as IconTable,
	Upload as IconUpload,
} from '@mui/icons-material';

// components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { ExcelStyledTable } from './ExcelStyledTable';
import { Empty } from '../../../ui/Empty/Empty';

// parser
import { parseExcelStyled, StyledRow, SheetMetadata, StyledSheetInfo } from './parseExcelStyled';

// csv fallback
import { parseCsv } from '../../../../utils/files';

// Types

interface ExcelUploadRecord {
	id: number;
	projectId: number;
	name: string;
	description: string | null;
	fileName: string | null;
	sheetName: string | null;
	documentType: string;
	headers: string[];
	rows: StyledRow[];
	metadata: SheetMetadata;
	linkedEntityType: string | null;
	linkedEntityId: number | null;
	status: string;
	rowCount?: number;
	createdBy: number | null;
	modifiedBy: number | null;
	created: string;
	modified: string | null;
}

// Document type labels
const DOCUMENT_TYPES: Record<string, string> = {
	general: 'General',
	annual_work_plan: 'Annual Work Plan',
	procurement_plan: 'Procurement Plan',
	activity_plan: 'Activity Plan',
	budget: 'Budget',
	other: 'Other',
};

// CSV to StyledRow conversion (no styling for CSV files)

function csvToStyledRows(
	csvRows: Record<string, unknown>[],
	headers: string[]
): { styledRows: StyledRow[]; columns: string[]; metadata: SheetMetadata } {
	const columns = headers.map((_, i) => {
		let result = '';
		let c = i + 1;
		while (c > 0) {
			c--;
			result = String.fromCharCode(65 + (c % 26)) + result;
			c = Math.floor(c / 26);
		}
		return result;
	});

	// Header row (row 0)
	const headerRow: StyledRow = { rowIndex: 0, cells: {} };
	columns.forEach((col, i) => {
		headerRow.cells[col] = {
			value: headers[i],
			col,
			style: { bold: true, fill: '#e8e8e8' },
		};
	});

	// Data rows
	const styledRows: StyledRow[] = [headerRow];
	csvRows.forEach((row, rowIdx) => {
		const styledRow: StyledRow = { rowIndex: rowIdx + 1, cells: {} };
		columns.forEach((col, colIdx) => {
			const header = headers[colIdx];
			const value = row[header];
			styledRow.cells[col] = {
				value: value != null ? String(value) : null,
				col,
			};
		});
		styledRows.push(styledRow);
	});

	return {
		styledRows,
		columns,
		metadata: {
			mergedCells: [],
			columnWidths: Object.fromEntries(columns.map((c) => [c, 120])),
			hiddenColumns: [],
			totalColumns: columns.length,
			totalRows: csvRows.length + 1,
			columnOrder: columns,
		},
	};
}

// Main Component

export const CustomTemplates = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const navigate = useNavigate();
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);

	// View mode
	const [viewMode, setViewMode] = useState<'list' | 'upload'>('list');

	// Upload state
	const [isDragging, setIsDragging] = useState(false);
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const [isParsingFile, setIsParsingFile] = useState(false);
	const [sheets, setSheets] = useState<StyledSheetInfo[]>([]);
	const [selectedSheet, setSelectedSheet] = useState('');
	const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
	const [parsedHeaderLabels, setParsedHeaderLabels] = useState<Record<string, string>>({});
	const [parsedRows, setParsedRows] = useState<StyledRow[]>([]);
	const [parsedMetadata, setParsedMetadata] = useState<SheetMetadata>({
		mergedCells: [],
		columnWidths: {},
		hiddenColumns: [],
		totalColumns: 0,
		totalRows: 0,
		columnOrder: [],
	});

	// Save dialog
	const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
	const [saveName, setSaveName] = useState('');
	const [saveDescription, setSaveDescription] = useState('');
	const [saveDocumentType, setSaveDocumentType] = useState('general');
	const [isSaving, setIsSaving] = useState(false);

	// File input ref
	const fileInputRef = useRef<HTMLInputElement>(null);

	// API URL — strip /v1 suffix so v2 endpoints resolve correctly
	const apiUrl =
		process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '') || process.env.REACT_APP_API_URL;

	// Auth headers — required for all API calls
	const getHeaders = useCallback((includeContentType = true) => {
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		const headers: Record<string, string> = {
			Authorization: `Bearer ${accessToken}`,
			User: String(idToken),
		};
		if (includeContentType) {
			headers['Content-Type'] = 'application/json';
		}
		return headers;
	}, []);

	// ==========================================================================
	// Fetch saved uploads list
	// ==========================================================================

	const [savedUploads, setSavedUploads] = useState<ExcelUploadRecord[]>([]);
	const [isLoadingList, setIsLoadingList] = useState(false);

	const fetchList = useCallback(async () => {
		if (!project?.id) return;
		setIsLoadingList(true);
		try {
			const res = await fetch(`${apiUrl}/v2/spreadsheet-data?projectId=${project.id}`, {
				headers: getHeaders(false),
			});
			const json = await res.json();
			if (json.data) {
				setSavedUploads(json.data);
			}
		} catch (err) {
			console.error('Failed to fetch excel uploads', err);
		} finally {
			setIsLoadingList(false);
		}
	}, [apiUrl, getHeaders, project?.id]);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	// ==========================================================================
	// File Handling — Parse with Full Styling
	// ==========================================================================

	const handleFileSelect = useCallback(
		async (file: File) => {
			setUploadFile(file);
			setIsParsingFile(true);

			try {
				const ext = file.name.split('.').pop()?.toLowerCase();

				if (ext === 'csv') {
					const text = await file.text();
					const { rows, headers } = parseCsv({ string: text });
					const { styledRows, columns, metadata } = csvToStyledRows(rows, headers);
					setParsedHeaders(columns);
					setParsedHeaderLabels(Object.fromEntries(columns.map((c, i) => [c, headers[i]])));
					setParsedRows(styledRows);
					setParsedMetadata(metadata);
					setSheets([]);
					setSelectedSheet('');
				} else {
					const result = await parseExcelStyled({ file });
					setParsedHeaders(result.headers);
					setParsedHeaderLabels(result.headerLabels);
					setParsedRows(result.rows);
					setParsedMetadata(result.metadata);
					setSheets(result.sheets);
					setSelectedSheet(result.selectedSheet);
				}

				setViewMode('upload');
			} catch (err) {
				setBanner({
					message: `Failed to parse file: ${err instanceof Error ? err.message : err}`,
					severity: 'error',
				});
			} finally {
				setIsParsingFile(false);
			}
		},
		[setBanner]
	);

	const handleSheetChange = useCallback(
		async (sheetName: string) => {
			if (!uploadFile) return;
			setSelectedSheet(sheetName);
			setIsParsingFile(true);

			try {
				const result = await parseExcelStyled({ file: uploadFile, sheetName });
				setParsedHeaders(result.headers);
				setParsedHeaderLabels(result.headerLabels);
				setParsedRows(result.rows);
				setParsedMetadata(result.metadata);
			} catch (err) {
				setBanner({
					message: `Failed to parse sheet: ${err instanceof Error ? err.message : err}`,
					severity: 'error',
				});
			} finally {
				setIsParsingFile(false);
			}
		},
		[uploadFile, setBanner]
	);

	// Drag & Drop
	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);
			const file = e.dataTransfer.files[0];
			if (file) handleFileSelect(file);
		},
		[handleFileSelect]
	);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file) handleFileSelect(file);
		},
		[handleFileSelect]
	);

	// ==========================================================================
	// Save to Backend
	// ==========================================================================

	const handleSave = useCallback(async () => {
		if (!project?.id) return;
		setIsSaving(true);

		try {
			const payload = {
				projectId: project.id,
				name: saveName,
				description: saveDescription || undefined,
				fileName: uploadFile?.name || undefined,
				sheetName: selectedSheet || undefined,
				documentType: saveDocumentType,
				headers: parsedHeaders,
				rows: parsedRows,
				metadata: parsedMetadata,
			};

			const res = await fetch(`${apiUrl}/v2/spreadsheet-data`, {
				method: 'POST',
				headers: getHeaders(),
				body: JSON.stringify(payload),
			});
			const json = await res.json();

			if (json.error) {
				throw new Error(json.error.message);
			}

			setBanner({
				message: `"${saveName}" saved successfully with ${parsedRows.length} rows.`,
				severity: 'success',
			});

			setIsSaveDialogOpen(false);
			setSaveName('');
			setSaveDescription('');
			setSaveDocumentType('general');
			setViewMode('list');
			fetchList();
		} catch (err) {
			setBanner({
				message: `Failed to save: ${err instanceof Error ? err.message : err}`,
				severity: 'error',
			});
		} finally {
			setIsSaving(false);
		}
	}, [
		apiUrl,
		fetchList,
		getHeaders,
		parsedHeaders,
		parsedMetadata,
		parsedRows,
		project?.id,
		saveDescription,
		saveDocumentType,
		saveName,
		selectedSheet,
		setBanner,
		uploadFile?.name,
	]);

	// ==========================================================================
	// Load & Edit a saved record
	// ==========================================================================

	const handleOpenRecord = useCallback(
		(record: ExcelUploadRecord) => {
			navigate(`${record.id}`);
		},
		[navigate]
	);

	const handleDeactivateRecord = useCallback(
		async (id: number) => {
			if (!window.confirm('Are you sure you want to deactivate this upload?')) return;

			try {
				const res = await fetch(`${apiUrl}/v2/spreadsheet-data/${id}`, {
					method: 'PUT',
					headers: getHeaders(true),
					body: JSON.stringify({ status: 'DEACTIVATED' }),
				});
				const json = await res.json();
				if (json.error) throw new Error(json.error.message);

				setBanner({
					message: 'Upload deactivated.',
					severity: 'success',
				});
				fetchList();
			} catch (err) {
				setBanner({
					message: `Failed to deactivate: ${err instanceof Error ? err.message : err}`,
					severity: 'error',
				});
			}
		},
		[apiUrl, fetchList, getHeaders, setBanner]
	);

	// ==========================================================================
	// Render — List View
	// ==========================================================================

	const renderListView = () => (
		<Box>
			<HeaderListingPage
				icon={IconTable}
				title="Custom Templates"
				subtitle={t('spendPlan.customTemplates.subtitle')}
				variant="workPlan"
				actions={
					<Stack direction="row" spacing={1.5}>
						<Button
							variant="outlined"
							startIcon={<IconRefresh />}
							onClick={fetchList}
							disabled={isLoadingList}
							sx={{ borderRadius: 3, fontWeight: 600, px: 3, py: 1.25, textTransform: 'none' }}>
							{t('spendPlan.customTemplates.refresh')}
						</Button>
						<Button
							variant="contained"
							startIcon={<IconUpload />}
							onClick={() => setViewMode('upload')}
							sx={{ borderRadius: 3, fontWeight: 600, px: 3, py: 1.25, textTransform: 'none' }}>
							{t('spendPlan.customTemplates.uploadExcel')}
						</Button>
					</Stack>
				}
				stats={[
					{ label: t('spendPlan.customTemplates.totalUploads'), value: savedUploads.length },
					{
						label: t('active'),
						value: savedUploads.filter((u) => u.status === 'ACTIVE').length,
					},
					{
						label: t('draft'),
						value: savedUploads.filter((u) => u.status === 'DRAFT').length,
					},
				]}
			/>

			<Box sx={{ p: 3 }}>
				{isLoadingList ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
						<CircularProgress />
					</Box>
				) : savedUploads.length === 0 ? (
					<Empty
						IconCircleProps={{ icon: <IconExcel sx={{ fontSize: 32 }} /> }}
						title={{ children: t('spendPlan.customTemplates.noDataYet') }}
						content={{
							children:
								t('spendPlan.customTemplates.noDataDescription'),
						}}
						actions={[
							{
								children: t('spendPlan.customTemplates.uploadFirstFile'),
								variant: 'contained',
								startIcon: <IconUpload />,
								onClick: () => setViewMode('upload'),
							},
						]}
						sx={{ py: 6 }}
					/>
				) : (
					<Grid container spacing={2}>
						{savedUploads.map((record) => (
							<Grid item xs={12} md={6} lg={4} key={record.id}>
								<Card
									sx={{
										cursor: 'pointer',
										transition: 'all 0.2s',
										border: '1px solid',
										borderColor: 'divider',
										'&:hover': {
											borderColor: theme.palette.primary.main,
											boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
											transform: 'translateY(-2px)',
										},
									}}
									onClick={() => handleOpenRecord(record)}>
									<CardContent>
										<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
											<Stack direction="row" spacing={1.5} alignItems="center">
												<Box
													sx={{
														width: 40,
														height: 40,
														borderRadius: 2,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														bgcolor: alpha(theme.palette.primary.main, 0.1),
													}}>
													<IconExcel sx={{ color: theme.palette.primary.main }} />
												</Box>
												<Box>
													<Typography variant="subtitle1" fontWeight={600} noWrap>
														{record.name}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{record.fileName || 'No file name'}
													</Typography>
												</Box>
											</Stack>
											<Tooltip title="Deactivate">
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														handleDeactivateRecord(record.id);
													}}>
													<IconDelete fontSize="small" />
												</IconButton>
											</Tooltip>
										</Stack>

										<Divider sx={{ my: 1.5 }} />

										<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
											<Chip
												label={DOCUMENT_TYPES[record.documentType] || record.documentType}
												size="small"
												variant="outlined"
											/>
											<Chip
												label={`${record.rowCount ?? '?'} rows`}
												size="small"
												color="primary"
												variant="outlined"
											/>
											<Chip
												label={record.status}
												size="small"
												color={
													record.status === 'ACTIVE'
														? 'success'
														: record.status === 'ARCHIVED'
															? 'default'
															: 'warning'
												}
											/>
											{record.sheetName && (
												<Chip
													label={`Sheet: ${record.sheetName}`}
													size="small"
													variant="outlined"
												/>
											)}
										</Stack>

										{record.description && (
											<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
												{record.description}
											</Typography>
										)}

										<Typography
											variant="caption"
											color="text.disabled"
											sx={{ mt: 1, display: 'block' }}>
											Uploaded {new Date(record.created).toLocaleDateString()}
										</Typography>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				)}
			</Box>
		</Box>
	);

	// ==========================================================================
	// Render — Upload View
	// ==========================================================================

	const renderUploadView = () => (
		<Box>
			<HeaderListingPage
				icon={IconCloudUpload}
				title={t('spendPlan.customTemplates.uploadTitle')}
				subtitle={t('spendPlan.customTemplates.uploadSubtitle')}
				variant="activity"
				actions={
					<Button
						variant="outlined"
						startIcon={<IconBack />}
						sx={{ borderRadius: 3, fontWeight: 600, px: 3, py: 1.25, textTransform: 'none' }}
						onClick={() => {
							setViewMode('list');
							setUploadFile(null);
							setParsedHeaders([]);
							setParsedRows([]);
							setSheets([]);
							setParsedMetadata({
								mergedCells: [],
								columnWidths: {},
								hiddenColumns: [],
								totalColumns: 0,
								totalRows: 0,
								columnOrder: [],
							});
						}}>
						Back to List
					</Button>
				}
			/>

			<Box sx={{ p: 3 }}>
				{/* Drop Zone */}
				{!uploadFile && (
					<Paper
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						sx={{
							p: 6,
							textAlign: 'center',
							border: '2px dashed',
							borderColor: isDragging ? theme.palette.primary.main : 'divider',
							borderRadius: 3,
							bgcolor: isDragging
								? alpha(theme.palette.primary.main, 0.08)
								: alpha(theme.palette.primary.main, 0.02),
							cursor: 'pointer',
							transition: 'all 0.2s',
							'&:hover': {
								borderColor: theme.palette.primary.main,
								bgcolor: alpha(theme.palette.primary.main, 0.05),
							},
						}}
						onClick={() => fileInputRef.current?.click()}>
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx,.xls,.csv"
							style={{ display: 'none' }}
							onChange={handleFileInputChange}
						/>
						<IconCloudUpload
							sx={{
								fontSize: 64,
								color: isDragging ? theme.palette.primary.main : 'text.disabled',
								mb: 2,
							}}
						/>
						<Typography variant="h6" gutterBottom>
							{isDragging ? 'Drop your file here' : 'Drag & Drop or Click to Upload'}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Supports .xlsx, .xls, and .csv files in any format with any columns. Styling is fully
							preserved.
						</Typography>
					</Paper>
				)}

				{/* Parsing indicator */}
				{isParsingFile && (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress />
					</Box>
				)}

				{/* File loaded — controls + styled preview */}
				{uploadFile && !isParsingFile && parsedRows.length > 0 && (
					<Box>
						{/* File info bar */}
						<Paper
							sx={{
								p: 2,
								mb: 2,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								borderLeft: `4px solid ${theme.palette.primary.main}`,
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<IconFile color="primary" />
								<Box>
									<Typography variant="subtitle2" fontWeight={600}>
										{uploadFile.name}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{parsedHeaders.length} columns &middot; {parsedRows.length} rows
										{parsedMetadata.mergedCells.length > 0 &&
											` \u00B7 ${parsedMetadata.mergedCells.length} merged regions`}
										{sheets.length > 1 && ` \u00B7 ${sheets.length} sheets`}
									</Typography>
								</Box>

								{/* Sheet Selector */}
								{sheets.length > 1 && (
									<FormControl size="small" sx={{ minWidth: 200 }}>
										<InputLabel>Sheet</InputLabel>
										<Select
											value={selectedSheet}
											label="Sheet"
											onChange={(e) => handleSheetChange(e.target.value)}>
											{sheets.map((s) => (
												<MenuItem key={s.name} value={s.name}>
													{s.name} ({s.rowCount} rows)
												</MenuItem>
											))}
										</Select>
									</FormControl>
								)}
							</Stack>

							<Stack direction="row" spacing={1}>
								<Button
									variant="outlined"
									size="small"
									onClick={() => {
										setUploadFile(null);
										setParsedHeaders([]);
										setParsedRows([]);
										setSheets([]);
										if (fileInputRef.current) fileInputRef.current.value = '';
									}}>
									Clear
								</Button>
								<Button
									variant="contained"
									startIcon={<IconSave />}
									onClick={() => {
										setSaveName(uploadFile.name.replace(/\.[^.]+$/, '') || 'Untitled');
										setIsSaveDialogOpen(true);
									}}>
									Save Data
								</Button>
							</Stack>
						</Paper>

						{/* Styled Table Preview */}
						<ExcelStyledTable
							rows={parsedRows}
							columns={parsedHeaders}
							metadata={parsedMetadata}
							headerLabels={parsedHeaderLabels}
							editable={false}
							maxHeight={650}
						/>
					</Box>
				)}
			</Box>

			{/* Save Dialog */}
			<Dialog
				open={isSaveDialogOpen}
				onClose={() => setIsSaveDialogOpen(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>Save Upload</DialogTitle>
				<DialogContent>
					<Stack spacing={2.5} sx={{ mt: 1 }}>
						<TextField
							label="Name"
							value={saveName}
							onChange={(e) => setSaveName(e.target.value)}
							fullWidth
							required
							placeholder="e.g., Annual Work Plan 2025"
						/>
						<TextField
							label="Description (optional)"
							value={saveDescription}
							onChange={(e) => setSaveDescription(e.target.value)}
							fullWidth
							multiline
							rows={2}
							placeholder="Brief description of this upload..."
						/>
						<FormControl fullWidth>
							<InputLabel>Document Type</InputLabel>
							<Select
								value={saveDocumentType}
								label="Document Type"
								onChange={(e) => setSaveDocumentType(e.target.value)}>
								{Object.entries(DOCUMENT_TYPES).map(([value, label]) => (
									<MenuItem key={value} value={value}>
										{label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box
							sx={{
								p: 2,
								bgcolor: alpha(theme.palette.info.main, 0.08),
								borderRadius: 2,
							}}>
							<Typography variant="body2" color="text.secondary">
								<strong>{parsedRows.length}</strong> rows with{' '}
								<strong>{parsedHeaders.length}</strong> columns will be saved with all styling
								{parsedMetadata.mergedCells.length > 0 &&
									` (${parsedMetadata.mergedCells.length} merged regions)`}
								{selectedSheet && ` from sheet "${selectedSheet}"`}.
							</Typography>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setIsSaveDialogOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleSave}
						disabled={!saveName.trim() || isSaving}
						startIcon={isSaving ? <CircularProgress size={18} /> : <IconSave />}>
						{isSaving ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);

	// ==========================================================================
	// Render
	// ==========================================================================

	return (
		<Box sx={{ minHeight: '100%', bgcolor: 'background.default' }}>
			{viewMode === 'list' && renderListView()}
			{viewMode === 'upload' && renderUploadView()}
		</Box>
	);
};
